import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Package, ShoppingBag, Mail, Plus, Edit2, Trash2, X, Check,
  ChefHat, Truck, Home as HomeIcon, ChevronDown, Search, AlertCircle, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import Logo from "../components/Logo";
import ImageUploader from "../components/ImageUploader";
import { resolveImageUrl } from "../lib/api";

const resolveImg = resolveImageUrl;

const STATUS_OPTIONS = [
  { value: "received", label: "Order Received", icon: ShoppingBag },
  { value: "baking", label: "Baking", icon: ChefHat },
  { value: "packaging", label: "Packaging", icon: Package },
  { value: "out_for_delivery", label: "Out For Delivery", icon: Truck },
  { value: "delivered", label: "Delivered", icon: HomeIcon },
  { value: "cancelled", label: "Cancelled", icon: X },
];

const STATUS_META = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s]));

const TABS = [
  { id: "orders", label: "Orders", icon: ShoppingBag },
  { id: "products", label: "Products", icon: Package },
  { id: "site-images", label: "Site Images", icon: ImageIcon },
  { id: "contacts", label: "Messages", icon: Mail },
];

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function AdminDashboard() {
  const { user, logout, authedRequest } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-shay-cream" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-shay-navy text-shay-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo variant="plain" size="sm" />
            <div className="hidden md:block">
              <p className="text-[10px] tracking-widest uppercase text-shay-gold">Admin Console</p>
              <p className="text-sm">{user?.username ? `Logged in as ${user.username}` : ""}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-shay-gold/40 text-shay-cream text-xs tracking-widest uppercase hover:bg-shay-gold hover:text-shay-navy transition-colors"
          >
            <LogOut size={14} /> Log Out
          </button>
        </div>

        {/* Tabs */}
        <div className="border-t border-shay-gold/20">
          <div className="max-w-7xl mx-auto px-6 md:px-8 flex gap-1 overflow-x-auto no-scrollbar">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`px-5 py-4 text-xs tracking-widest uppercase font-medium border-b-2 transition-colors ${
                  tab === t.id ? "border-shay-gold text-shay-gold" : "border-transparent text-shay-cream/60 hover:text-shay-cream"
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  <t.icon size={14} /> {t.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-6 md:px-8 py-10 md:py-12">
        {tab === "orders" && <OrdersTab authedRequest={authedRequest} />}
        {tab === "products" && <ProductsTab authedRequest={authedRequest} />}
        {tab === "site-images" && <SiteImagesTab authedRequest={authedRequest} />}
        {tab === "contacts" && <ContactsTab authedRequest={authedRequest} />}
      </main>
    </div>
  );
}

// =================== ORDERS ===================
function OrdersTab({ authedRequest }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedRequest.get("/admin/orders");
      setOrders(data);
    } catch (e) {
      toast.error("Failed to load orders", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  }, [authedRequest]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (orderNumber, status) => {
    try {
      const { data } = await authedRequest.patch(`/admin/orders/${orderNumber}`, { status });
      setOrders((prev) => prev.map((o) => (o.order_number === orderNumber ? data : o)));
      toast.success(`Order ${orderNumber} → ${STATUS_META[status]?.label || status}`);
    } catch (e) {
      toast.error("Couldn't update status", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  const deleteOrder = async (orderNumber) => {
    if (!window.confirm(`Delete order ${orderNumber}? This cannot be undone.`)) return;
    try {
      await authedRequest.delete(`/admin/orders/${orderNumber}`);
      setOrders((prev) => prev.filter((o) => o.order_number !== orderNumber));
      toast.success(`Order ${orderNumber} deleted`);
    } catch (e) {
      toast.error("Couldn't delete order");
    }
  };

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      const matchesStatus = statusFilter === "all" || o.status === statusFilter;
      const q = search.trim().toLowerCase();
      const matchesSearch = !q ||
        o.order_number.toLowerCase().includes(q) ||
        o.customer?.email?.toLowerCase().includes(q) ||
        `${o.customer?.first_name} ${o.customer?.last_name}`.toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [orders, statusFilter, search]);

  return (
    <section data-testid="admin-orders-tab">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow">Orders</p>
          <h1 className="font-display text-4xl md:text-5xl text-shay-navy mt-2 tracking-tight">
            All Orders <span className="font-script text-shay-gold text-3xl ml-2">({orders.length})</span>
          </h1>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-shay-navy/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by order #, name, email"
              data-testid="admin-orders-search"
              className="pl-9 pr-4 py-2.5 w-72 max-w-full bg-white border border-shay-gold/30 rounded-full text-sm focus:outline-none focus:border-shay-gold"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="admin-orders-status-filter"
            className="px-4 py-2.5 bg-white border border-shay-gold/30 rounded-full text-sm focus:outline-none focus:border-shay-gold"
          >
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-shay-chocolate/60">Loading orders...</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-shay-gold/20 p-16 text-center" data-testid="admin-orders-empty">
          <ShoppingBag size={32} className="mx-auto text-shay-gold/50 mb-4" />
          <p className="font-display text-2xl text-shay-navy">No orders match.</p>
          <p className="mt-2 text-sm text-shay-chocolate/60">Try changing your filters.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-orders-list">
          {filtered.map((order) => {
            const isOpen = expanded === order.order_number;
            const StatusIcon = STATUS_META[order.status]?.icon || ShoppingBag;
            return (
              <article
                key={order.order_number}
                className="bg-white border border-shay-gold/20 overflow-hidden"
                data-testid={`admin-order-${order.order_number}`}
              >
                <div className="grid grid-cols-12 gap-4 items-center px-5 md:px-7 py-4">
                  <div className="col-span-12 md:col-span-3">
                    <p className="font-display text-lg text-shay-navy tracking-wider">{order.order_number}</p>
                    <p className="text-xs text-shay-chocolate/60 mt-0.5">{fmtDate(order.created_at)}</p>
                  </div>
                  <div className="col-span-7 md:col-span-3 min-w-0">
                    <p className="font-medium text-sm truncate">{order.customer.first_name} {order.customer.last_name}</p>
                    <p className="text-xs text-shay-chocolate/60 truncate">{order.customer.email}</p>
                  </div>
                  <div className="col-span-5 md:col-span-2 text-right md:text-left">
                    <p className="font-display text-xl text-shay-navy">{fmt(order.total)}</p>
                    <p className="text-xs text-shay-chocolate/60">{order.items.reduce((s, i) => s + i.quantity, 0)} items</p>
                  </div>
                  <div className="col-span-9 md:col-span-3">
                    <div className="relative">
                      <select
                        value={order.status}
                        onChange={(e) => updateStatus(order.order_number, e.target.value)}
                        data-testid={`admin-order-status-${order.order_number}`}
                        className="appearance-none w-full pl-9 pr-9 py-2.5 bg-shay-cream border border-shay-gold/30 rounded-full text-xs uppercase tracking-widest font-medium text-shay-navy focus:outline-none focus:border-shay-gold cursor-pointer"
                      >
                        {STATUS_OPTIONS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                      </select>
                      <StatusIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-shay-gold pointer-events-none" />
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-shay-navy/50 pointer-events-none" />
                    </div>
                  </div>
                  <div className="col-span-3 md:col-span-1 flex justify-end gap-1">
                    <button
                      onClick={() => setExpanded(isOpen ? null : order.order_number)}
                      data-testid={`admin-order-toggle-${order.order_number}`}
                      className="h-9 w-9 grid place-items-center rounded-full hover:bg-shay-cream text-shay-navy/70"
                      aria-label={isOpen ? "Collapse" : "Expand"}
                    >
                      <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    <button
                      onClick={() => deleteOrder(order.order_number)}
                      data-testid={`admin-order-delete-${order.order_number}`}
                      className="h-9 w-9 grid place-items-center rounded-full hover:bg-red-50 text-red-600/70"
                      aria-label="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="px-5 md:px-7 pb-6 pt-2 border-t border-shay-gold/15 grid md:grid-cols-3 gap-6 bg-shay-cream/40">
                    <div>
                      <p className="eyebrow">Items</p>
                      <ul className="mt-3 space-y-2">
                        {order.items.map((it) => (
                          <li key={it.product_id} className="flex justify-between text-sm">
                            <span>{it.quantity} × {it.name}</span>
                            <span className="text-shay-navy">{fmt(it.price * it.quantity)}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-4 pt-3 border-t border-shay-gold/15 text-sm space-y-1">
                        <div className="flex justify-between text-shay-chocolate/70"><span>Subtotal</span><span>{fmt(order.subtotal)}</span></div>
                        {order.discount > 0 && <div className="flex justify-between text-shay-gold"><span>Discount{order.promo_code ? ` (${order.promo_code})` : ""}</span><span>-{fmt(order.discount)}</span></div>}
                        <div className="flex justify-between text-shay-chocolate/70"><span>Tax</span><span>{fmt(order.tax)}</span></div>
                        <div className="flex justify-between text-shay-chocolate/70"><span>Delivery</span><span>{fmt(order.shipping)}</span></div>
                        <div className="flex justify-between font-medium text-shay-navy"><span>Total</span><span>{fmt(order.total)}</span></div>
                      </div>
                    </div>
                    <div>
                      <p className="eyebrow">Customer</p>
                      <p className="mt-3 text-sm font-medium">{order.customer.first_name} {order.customer.last_name}</p>
                      <p className="text-sm text-shay-chocolate/75">{order.customer.email}</p>
                      <p className="text-sm text-shay-chocolate/75">{order.customer.phone}</p>
                    </div>
                    <div>
                      <p className="eyebrow">Delivery</p>
                      <p className="mt-3 text-sm">{order.shipping_address.line1}</p>
                      <p className="text-sm">{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</p>
                      {order.delivery_notes && (
                        <p className="mt-3 text-sm text-shay-chocolate/75 italic">"{order.delivery_notes}"</p>
                      )}
                      {order.estimated_delivery && (
                        <p className="mt-3 text-xs uppercase tracking-widest text-shay-brown">ETA: {order.estimated_delivery}</p>
                      )}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

// =================== PRODUCTS ===================
const EMPTY_PRODUCT = {
  name: "", category: "cookies", price: 0, image: "",
  description: "", tags: [], is_active: true,
};

function ProductsTab({ authedRequest }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | productId
  const [draft, setDraft] = useState(EMPTY_PRODUCT);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedRequest.get("/admin/products");
      setProducts(data);
    } catch (e) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [authedRequest]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing("new"); setDraft(EMPTY_PRODUCT); };

  const startEdit = (p) => {
    setEditing(p.id);
    setDraft({
      name: p.name, category: p.category, price: p.price,
      image: p.image, description: p.description,
      tags: p.tags || [], is_active: p.is_active,
    });
  };

  const cancelEdit = () => { setEditing(null); setDraft(EMPTY_PRODUCT); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...draft, price: parseFloat(draft.price) || 0 };
    try {
      if (editing === "new") {
        const { data } = await authedRequest.post("/admin/products", payload);
        setProducts((prev) => [...prev, data]);
        toast.success(`"${data.name}" added`);
      } else {
        const { data } = await authedRequest.put(`/admin/products/${editing}`, payload);
        setProducts((prev) => prev.map((p) => (p.id === editing ? data : p)));
        toast.success(`"${data.name}" updated`);
      }
      cancelEdit();
    } catch (e) {
      toast.error("Save failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await authedRequest.delete(`/admin/products/${p.id}`);
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
      toast.success(`"${p.name}" deleted`);
    } catch (e) {
      toast.error("Couldn't delete");
    }
  };

  return (
    <section data-testid="admin-products-tab">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow">Products</p>
          <h1 className="font-display text-4xl md:text-5xl text-shay-navy mt-2 tracking-tight">
            The Menu <span className="font-script text-shay-gold text-3xl ml-2">({products.length})</span>
          </h1>
        </div>
        <button onClick={startNew} className="btn-gold" data-testid="admin-product-new-btn">
          <Plus size={16} /> Add Product
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white border-2 border-shay-gold p-7 mb-8" data-testid="admin-product-form">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-2xl text-shay-navy">
              {editing === "new" ? "Add a new product" : "Edit product"}
            </h2>
            <button type="button" onClick={cancelEdit} className="h-9 w-9 grid place-items-center rounded-full hover:bg-shay-cream" aria-label="Close" data-testid="admin-product-cancel-btn">
              <X size={18} />
            </button>
          </div>
          <div className="grid md:grid-cols-12 gap-x-6 gap-y-4">
            <div className="md:col-span-7">
              <label className="text-[10px] uppercase tracking-widest text-shay-brown">Name</label>
              <input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="field-input mt-1" data-testid="admin-product-name" />
            </div>
            <div className="md:col-span-3">
              <label className="text-[10px] uppercase tracking-widest text-shay-brown">Category</label>
              <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="field-input mt-1" data-testid="admin-product-category">
                <option value="cookies">Cookies</option>
                <option value="oatmeal">Oatmeal</option>
                <option value="brownies">Brownies</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-shay-brown">Price (USD)</label>
              <input required type="number" step="0.01" min="0" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} className="field-input mt-1" data-testid="admin-product-price" />
            </div>
            <div className="md:col-span-12">
              <label className="text-[10px] uppercase tracking-widest text-shay-brown mb-2 block">Product Image</label>
              <ImageUploader
                value={draft.image}
                onChange={(url) => setDraft({ ...draft, image: url })}
                authedRequest={authedRequest}
                testIdPrefix="admin-product-image"
              />
            </div>
            <div className="md:col-span-12">
              <label className="text-[10px] uppercase tracking-widest text-shay-brown">Description</label>
              <textarea required rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="field-input mt-1 resize-none" data-testid="admin-product-description" />
            </div>
            <div className="md:col-span-8">
              <label className="text-[10px] uppercase tracking-widest text-shay-brown">Tags (comma-separated: signature, bestseller, gluten-free)</label>
              <input
                value={draft.tags.join(", ")}
                onChange={(e) => setDraft({ ...draft, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                className="field-input mt-1"
                data-testid="admin-product-tags"
              />
            </div>
            <div className="md:col-span-4 flex items-end">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={draft.is_active} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} data-testid="admin-product-active" />
                <span className="text-sm">Active (visible on menu)</span>
              </label>
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <button type="submit" className="btn-gold" data-testid="admin-product-save-btn">
              <Check size={16} /> {editing === "new" ? "Add Product" : "Save Changes"}
            </button>
            <button type="button" onClick={cancelEdit} className="btn-ghost-navy">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-shay-chocolate/60">Loading products...</p>
      ) : products.length === 0 ? (
        <div className="bg-white border border-shay-gold/20 p-16 text-center">
          <Package size={32} className="mx-auto text-shay-gold/50 mb-4" />
          <p className="font-display text-2xl text-shay-navy">No products yet.</p>
          <button onClick={startNew} className="btn-gold mt-5">Add your first product</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-products-grid">
          {products.map((p) => (
            <article key={p.id} className="bg-white border border-shay-gold/20 overflow-hidden flex flex-col" data-testid={`admin-product-${p.id}`}>
              <div className="aspect-[4/3] bg-shay-cream-deep overflow-hidden relative">
                <img src={resolveImg(p.image)} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                {!p.is_active && (
                  <span className="absolute top-3 left-3 bg-shay-navy text-shay-gold text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full">Hidden</span>
                )}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <p className="eyebrow">{p.category}</p>
                <h3 className="font-display text-lg text-shay-navy mt-1 leading-tight">{p.name}</h3>
                <p className="mt-2 text-xs text-shay-chocolate/70 line-clamp-2 flex-1">{p.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <p className="font-display text-xl text-shay-navy">{fmt(p.price)}</p>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(p)} data-testid={`admin-product-edit-${p.id}`} className="h-9 w-9 grid place-items-center rounded-full hover:bg-shay-cream text-shay-navy/70" aria-label="Edit">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => remove(p)} data-testid={`admin-product-delete-${p.id}`} className="h-9 w-9 grid place-items-center rounded-full hover:bg-red-50 text-red-600/70" aria-label="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}


// =================== SITE IMAGES ===================
function SiteImagesTab({ authedRequest }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [draftUrl, setDraftUrl] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedRequest.get("/admin/site-images");
      setImages(data);
    } catch (e) {
      toast.error("Failed to load site images", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  }, [authedRequest]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (image) => {
    setEditingKey(image.key);
    setDraftUrl(image.url || "");
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraftUrl("");
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await authedRequest.put(`/admin/site-images/${editingKey}`, { url: draftUrl });
      setImages((prev) => prev.map((image) => (image.key === editingKey ? data : image)));
      toast.success(`Updated ${data.label}`);
      cancelEdit();
    } catch (err) {
      toast.error("Image update failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const grouped = useMemo(() => images.reduce((acc, image) => {
    const group = image.page || "Other";
    acc[group] = acc[group] || [];
    acc[group].push(image);
    return acc;
  }, {}), [images]);

  return (
    <section data-testid="admin-site-images-tab">
      <div className="mb-8">
        <p className="eyebrow">Site Images</p>
        <h1 className="font-display text-4xl md:text-5xl text-shay-navy mt-2 tracking-tight">
          Picture Library <span className="font-script text-shay-gold text-3xl ml-2">({images.length})</span>
        </h1>
        <p className="mt-3 text-shay-chocolate/70 max-w-2xl">
          Update every non-product picture used around the public site. Product photos are still edited from the Products tab.
        </p>
      </div>

      {loading ? (
        <p className="text-shay-chocolate/60">Loading site images...</p>
      ) : (
        <div className="space-y-10">
          {Object.entries(grouped).map(([page, pageImages]) => (
            <div key={page}>
              <h2 className="font-display text-2xl text-shay-navy mb-4">{page}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {pageImages.map((image) => {
                  const isEditing = editingKey === image.key;
                  return (
                    <article key={image.key} className="bg-white border border-shay-gold/20 p-5" data-testid={`admin-site-image-${image.key}`}>
                      <div className="aspect-[4/3] bg-shay-cream-deep overflow-hidden mb-4">
                        <img src={resolveImg(image.url)} alt={image.label} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="eyebrow">{image.key}</p>
                          <h3 className="font-display text-xl text-shay-navy mt-1">{image.label}</h3>
                          {image.description && <p className="mt-2 text-sm text-shay-chocolate/70">{image.description}</p>}
                        </div>
                        {!isEditing && (
                          <button onClick={() => startEdit(image)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-shay-cream text-shay-navy/70" aria-label={`Edit ${image.label}`} data-testid={`admin-site-image-edit-${image.key}`}>
                            <Edit2 size={14} />
                          </button>
                        )}
                      </div>

                      {isEditing && (
                        <form onSubmit={save} className="mt-5 border-t border-shay-gold/15 pt-5" data-testid={`admin-site-image-form-${image.key}`}>
                          <ImageUploader
                            value={draftUrl}
                            onChange={setDraftUrl}
                            authedRequest={authedRequest}
                            testIdPrefix={`admin-site-image-${image.key}`}
                          />
                          <div className="mt-5 flex gap-3">
                            <button type="submit" className="btn-gold" data-testid={`admin-site-image-save-${image.key}`}>
                              <Check size={16} /> Save Image
                            </button>
                            <button type="button" onClick={cancelEdit} className="btn-ghost-navy">Cancel</button>
                          </div>
                        </form>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// =================== CONTACTS ===================
function ContactsTab({ authedRequest }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authedRequest.get("/admin/contacts");
        setContacts(data);
      } catch (e) {
        toast.error("Failed to load messages");
      } finally {
        setLoading(false);
      }
    })();
  }, [authedRequest]);

  return (
    <section data-testid="admin-contacts-tab">
      <div className="mb-8">
        <p className="eyebrow">Messages</p>
        <h1 className="font-display text-4xl md:text-5xl text-shay-navy mt-2 tracking-tight">
          Inbox <span className="font-script text-shay-gold text-3xl ml-2">({contacts.length})</span>
        </h1>
      </div>

      {loading ? (
        <p className="text-shay-chocolate/60">Loading messages...</p>
      ) : contacts.length === 0 ? (
        <div className="bg-white border border-shay-gold/20 p-16 text-center">
          <Mail size={32} className="mx-auto text-shay-gold/50 mb-4" />
          <p className="font-display text-2xl text-shay-navy">No messages yet.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-contacts-list">
          {contacts.map((c) => (
            <article key={c.id} className="bg-white border border-shay-gold/20 p-6" data-testid={`admin-contact-${c.id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <p className="font-display text-xl text-shay-navy">{c.name}</p>
                <p className="text-xs uppercase tracking-widest text-shay-chocolate/60">{fmtDate(c.created_at)}</p>
              </div>
              <p className="text-sm text-shay-chocolate/75">
                <a href={`mailto:${c.email}`} className="hover:text-shay-gold">{c.email}</a>
                {c.phone && <span className="ml-3">· {c.phone}</span>}
              </p>
              <p className="mt-4 text-shay-navy leading-relaxed whitespace-pre-wrap">{c.message}</p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
